/**
 * Émetteur : lit les fichiers et les pousse sur le canal.
 *
 * ## Ce qui fait la différence sur les gros fichiers
 *
 * - **`file.stream()` et non `FileReader`.** La lecture est incrémentale : le fichier
 *   n'est jamais entièrement en mémoire. `FileReader.readAsArrayBuffer` chargerait
 *   l'intégralité du fichier d'un coup.
 * - **Drainage avant chaque envoi.** C'est `TransferChannel` qui s'en charge ; l'émetteur
 *   n'a pas à connaître la notion de tampon.
 * - **Empreinte calculée pendant la lecture.** Un second passage sur le fichier pour
 *   calculer le SHA-256 doublerait le temps de lecture sur un support lent. On hache au
 *   fil de l'eau.
 *
 * ## Découpage
 *
 * En morceaux de 63 KiB. La trame complète (morceau + en-tête + tag) doit rester sous
 * 64 KiB, la limite SCTP qu'un navigateur peut imposer sans la négocier — Safari y
 * abandonne les messages silencieusement, et le récepteur verrait la séquence diverger.
 * Plus petit encore, l'en-tête et le tag pèsent proportionnellement plus lourd.
 */

import { CHUNK_SIZE, type FileMeta } from '@attrape/shared';
import { sha256 } from '@noble/hashes/sha2.js';
import type { TransferChannel } from './channel.js';
export interface SenderProgress {
  fileIndex: number;
  fileCount: number;
  fileName: string;
  fileBytes: number;
  fileTotal: number;
  totalSent: number;
  totalBytes: number;
  bytesPerSecond: number;
  etaSeconds: number | null;
}

export interface RunSenderOptions {
  channel: TransferChannel;
  files: File[];
  onProgress: (progress: SenderProgress) => void;
  signal: AbortSignal;
  /**
   * Plages à renvoyer, par index de fichier — fournies par le récepteur après une coupure.
   *
   * Un fichier absent de la table est envoyé **entièrement** : c'est le cas nominal d'un
   * premier transfert.
   */
  resumeRanges?: Map<number, [number, number][]>;
}

/** Construit les métadonnées transmises au récepteur avant tout envoi. */
export function buildFileMeta(files: File[]): FileMeta[] {
  return files.map((file, index) => ({
    // Identifiant stable et unique dans le lot : il sert de clé côté récepteur.
    id: `${index}-${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    mime: file.type || 'application/octet-stream',
    lastModified: file.lastModified,
  }));
}

/**
 * Envoie tous les fichiers, séquentiellement.
 *
 * Séquentiel et non parallèle : le débit est borné par la liaison montante de l'émetteur.
 * Ouvrir plusieurs flux simultanés ajouterait de la contention, brouillerait la
 * progression affichée et multiplierait l'occupation mémoire — sans rien accélérer.
 */
export async function runSender(options: RunSenderOptions): Promise<void> {
  const { channel, files, onProgress, signal, resumeRanges } = options;

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  let totalSent = 0;
  const startedAt = performance.now();
  let lastReport = 0;

  for (const [fileIndex, file] of files.entries()) {
    // Octets déjà envoyés pour ce fichier dans cette session. Sur une reprise, on ne
    // commence pas à la taille du fichier : on incrémentera au fil des morceaux réellement
    // partis, pour que la barre de progression du fichier courant ne reste pas à 100%.
    let fileBytes = 0;

    /**
     * Empreinte SHA-256 du fichier **entier**.
     *
     * Calculée en second passage sur le fichier, et non pendant l'envoi. C'est un coût
     * assumé : sur une reprise, seuls quelques morceaux sont envoyés, donc hacher « ce
     * qu'on envoie » produirait une empreinte partielle qui ne correspondrait à rien. Une
     * empreinte fausse vaut moins qu'aucune empreinte — elle ferait rejeter un fichier
     * correct, ou pire, accepter un fichier incomplet si les deux côtés se trompaient
     * identiquement.
     *
     * Le coût est celui d'une lecture séquentielle supplémentaire, sans transfert réseau :
     * négligeable devant l'envoi lui-même, et le seul moyen d'annoncer une empreinte juste.
     */
    const fullHash = await hashFile(file, signal);

    /**
     * Plages à envoyer pour ce fichier, en indices de morceaux.
     *
     * Sans reprise : une seule plage couvrant tout le fichier. Le reste de la boucle ne
     * connaît donc qu'un seul cas — ce qui évite d'écrire deux chemins de code dont un
     * seul serait testé en pratique.
     */
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const ranges: [number, number][] =
      resumeRanges?.get(fileIndex) ?? (totalChunks > 0 ? [[0, totalChunks - 1]] : []);

    const report = (force: boolean): void => {
      const now = performance.now();
      // Une remise à jour par tranche de 120 ms : assez fluide à l'œil, assez rare pour
      // ne pas faire de React le goulot d'étranglement sur un transfert rapide.
      if (!force && now - lastReport < 120) return;
      lastReport = now;

      const elapsedSeconds = Math.max(0.001, (now - startedAt) / 1000);
      const bytesPerSecond = totalSent / elapsedSeconds;
      const remaining = totalBytes - totalSent;

      onProgress({
        fileIndex,
        fileCount: files.length,
        fileName: file.name,
        fileBytes,
        fileTotal: file.size,
        totalSent,
        totalBytes,
        bytesPerSecond,
        etaSeconds: bytesPerSecond > 0 ? remaining / bytesPerSecond : null,
      });
    };

    report(true);

    // ── Envoi, plage par plage ────────────────────────────────────────────────
    //
    // Une reprise peut demander plusieurs plages disjointes (réseau instable). On lit le
    // fichier par tranches correspondantes, en sautant les morceaux déjà présents chez le
    // récepteur : le gain est proportionnel à ce qui a déjà été reçu.
    for (const [startChunk, endChunk] of ranges) {
      if (signal.aborted) throw new DOMException('Transfert annulé', 'AbortError');

      const startByte = startChunk * CHUNK_SIZE;
      const endByte = Math.min((endChunk + 1) * CHUNK_SIZE, file.size);
      if (startByte >= endByte) continue;

      /**
       * Tampon de coalescence : les blocs rendus par `file.stream()` n'ont aucune
       * raison de faire exactement `CHUNK_SIZE` octets (Chrome rend typiquement des
       * blocs de 64 KiB). Sans réassemblage, chaque bloc produirait un morceau de
       * `CHUNK_SIZE` suivi d'un reste court — le nombre de morceaux émis divergerait
       * alors de `chunkCountFor(size)`, et le récepteur refuserait les morceaux en
       * surplus. On accumule donc jusqu'à un morceau **complet**, et seul le dernier
       * morceau de la plage peut être court.
       */
      let pending: Uint8Array | null = null;
      let pendingBytes = 0;

      const flush = async (): Promise<void> => {
        if (pending === null || pendingBytes === 0) return;
        // `slice()` sur la partie utile : le tampon source peut être réutilisé par le
        // flux, et l'empreinte doit porter sur une copie stable.
        const chunk = new Uint8Array(pending.subarray(0, pendingBytes));
        pending = null;
        pendingBytes = 0;
        await channel.sendChunk(fileIndex, chunk, signal);
        totalSent += chunk.byteLength;
        fileBytes += chunk.byteLength;
        report(false);
      };

      const reader = file.slice(startByte, endByte).stream().getReader();
      try {
        while (true) {
          if (signal.aborted) throw new DOMException('Transfert annulé', 'AbortError');

          const { value, done } = await reader.read();
          if (done) {
            await flush();
            break;
          }
          if (!value || value.byteLength === 0) continue;

          let block: Uint8Array = value;
          while (block.byteLength > 0) {
            if (signal.aborted) throw new DOMException('Transfert annulé', 'AbortError');

            if (pending === null) {
              pending = new Uint8Array(CHUNK_SIZE);
              pendingBytes = 0;
            }
            const room = CHUNK_SIZE - pendingBytes;
            const taken = Math.min(room, block.byteLength);
            pending.set(block.subarray(0, taken), pendingBytes);
            pendingBytes += taken;
            block = block.subarray(taken);

            if (pendingBytes === CHUNK_SIZE) {
              await flush();
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    }

    report(true);

    // L'empreinte est envoyée après le dernier morceau : le récepteur peut ainsi
    // vérifier l'intégrité de ce qu'il vient d'écrire, puis fermer le fichier.
    //
    // Elle porte sur le fichier ENTIER, y compris les morceaux que le récepteur
    // possédait déjà : c'est la seule formulation qui ait un sens, et la seule qui
    // détecte un fichier dont une partie aurait été altérée entre deux sessions.
    await channel.sendControl({ t: 'file-done', fileIndex, sha256: fullHash }, signal);
  }

  await channel.sendControl({ t: 'all-done' }, signal);
}

/**
 * Calcule le SHA-256 d'un fichier complet, en flux.
 *
 * En flux et non par `arrayBuffer()` : un fichier de plusieurs gigaoctets ne doit pas
 * être chargé en mémoire juste pour être haché. C'est la même contrainte que pour l'envoi.
 */
async function hashFile(file: File, signal: AbortSignal): Promise<string> {
  const hasher = sha256.create();
  const reader = file.stream().getReader();
  try {
    while (true) {
      if (signal.aborted) throw new DOMException('Transfert annulé', 'AbortError');
      const { value, done } = await reader.read();
      if (done) break;
      if (value) hasher.update(value);
    }
  } finally {
    reader.releaseLock();
  }
  return toHex(hasher.digest());
}

/** Convertit un condensé en chaîne hexadécimale minuscule de 64 caractères. */
export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}
